import { useEffect, useState } from "react";
import {
  Tab,
  Box,
  Stack,
  Dialog,
  Select,
  MenuItem,
  Skeleton,
  Typography,
  IconButton,
  DialogTitle,
  FormControl,
  DialogContent,
  CircularProgress,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import Label from "components/Label";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { useListingEvents } from "hooks/useListingEvents";
import { Close } from "@material-ui/icons";
import { CalendarTasks } from "components/_dashboard/calendar/CalendarTasks";
import { ListingDetailsNew } from "components/_dashboard/listingsMatch/ListingDetailsNew";
import { MatchedBuyersTab } from "components/_dashboard/listingDetail/tabs/MatchedBuyersTab";
import { ListingCreativesTab } from "components/_dashboard/listingDetail/tabs/ListingCreativesTab";
import { ListingMakeoversTab } from "components/_dashboard/listingDetail/tabs/ListingMakeoversTab";
import { ListingWatermarksTab } from "components/_dashboard/listingDetail/tabs/ListingWatermarksTab";
import { convertListingToProcessedListingData } from "utils/listings/parser";
import { getFullAddress, parseListingAddressByDisplayMode } from "utils/listings/display";
import { ListingPublisherCard } from "../../ListingPublisherCard";
import { ListingTableFiles } from "../../table/rows/ListingTableFiles";
import { ListingDetailsGeneralTab } from "./ListingDetailsGeneralTab";
import { ListingDetailsMessagesTab } from "./ListingDetailsMessagesTab";
import { ListingTableSignatures } from "../../table/rows/ListingTableSignatures";
import { ListingNotesPopover } from "components/_dashboard/listingsBoard/ListingNotesPopover";
import { ListingOrgActivityIndicator } from "components/_dashboard/listings/orgActivity/ListingOrgActivityIndicator";
import { ListingActivitySection } from "components/_dashboard/listings/activity/ListingActivitySection";
import { ListingActivityIndicator } from "components/_dashboard/listings/activity/ListingActivityIndicator";
import { getRecordedListingContact, resolveListingSeller } from "utils/listings/listingSeller";
import { ContactPanelDrawer } from "components/_dashboard/contacts/detail/ContactPanelDrawer";
import { useRecordedListingContacts, withRecordedOwner } from "hooks/useRecordedListingContacts";
import useSavedListingSeller from "hooks/contacts/useSavedListingSeller";
import { savedOwnerOverAd } from "components/_dashboard/listingsBoard/boardSeller";
import type { ListingWithAdditionalData } from "../../../../../@types/listings/new";
import type { AlgoliaHitResult } from "components/_dashboard/listingsMatch/algoliaClient";
import { fetchListingById } from "utils/listings/fetchListings";
import type { CalendarEvent, ListingStatus } from "../../../../../@types/sharedSchema";

type ListingDetailsDialogProps = {
  open: boolean;
  listing?: ListingWithAdditionalData;
  algoliaListing?: AlgoliaHitResult;
  /**
   * True when the parent has opened the dialog optimistically and is still
   * resolving the Algolia listing. When true with no algoliaListing/listing,
   * the dialog renders a centered spinner instead of tabs to avoid mounting
   * MatchedBuyersTab and friends with empty data (which produces React errors
   * and pointless work).
   */
  isResolvingListing?: boolean;
  initialTab?: ListingDetailsTabType;
  isAuthorized?: boolean;
  handleClose: () => void;
  handleAddNewTask?: () => void;
  handleClickFavorite?: () => Promise<void>;
  handleEditListingDialogOpen?: (type?: "edit" | "duplicate") => () => void;
};

type TabsCount = {
  calls: number;
  sentMessages: number;
};

export function ListingDetailsDialog({
  open,
  listing,
  algoliaListing,
  isResolvingListing = false,
  initialTab,
  isAuthorized = false,
  handleClose,
  handleAddNewTask,
  handleClickFavorite,
  handleEditListingDialogOpen
}: ListingDetailsDialogProps): JSX.Element {
  const isViewMode = !listing && !!algoliaListing;
  const listingId = listing?.id ?? algoliaListing?.objectID ?? "";

  const isRentListing =
    (listing?.transactionType ?? algoliaListing?.transactionType ?? algoliaListing?.listingListedFor) ===
    "rent";

  const [currentTab, setCurrentTab] = useState<ListingDetailsTabType>(initialTab ?? "general");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tabsCount, setTabsCount] = useState<TabsCount | null>(null);
  const [loading, setLoading] = useState(true);

  const {
    user: { email: userEmail }
  } = useAuth();

  const { userSettings } = useFirestore();

  const listingEvents = useListingEvents(listingId);

  /**
   * WHO the seller side of this listing actually is.
   *
   * ## The bug this replaces
   *
   * In view mode the מפרסם card below read `algoliaListing.sellerName` /
   * `sellerPhoneNumber` / `agencyName` RAW. On a `webot` row the mirror Cloud
   * Function overwrites all three with the OWNING REALTOR's own details, so a
   * realtor opening HER OWN claimed listing — from the matches surfaces,
   * rental-to-sale, `ListingMatchChip`, the board's menu entries, a notification,
   * `OpenListingTriggerPage` — was shown her own name and phone under "מפרסם",
   * with live `tel:` and WhatsApp buttons pointing at herself. The owner she
   * recorded appeared nowhere. That is THE original complaint, one surface along
   * from where it was reported.
   *
   * ## Where the owner comes from
   *
   * The mirror row cannot carry him: `yad2-listings-latest` is `allow get: if
   * true` and the share-link favourites doc hands anonymous buyers those very
   * document ids, so mirroring the owner's private mobile would let a buyer call
   * him and cut the realtor out. He is read from the authority instead —
   * `real-estate-listings`, same document id — which is also strictly fresher
   * than the mirror. On the EDITABLE path we are already holding that document,
   * so no read is needed at all.
   */
  const isWebotMirrorRow = isViewMode && algoliaListing?.listingOrigin === "webot";
  const recordedContacts = useRecordedListingContacts(
    algoliaListing?.objectID ?? algoliaListing?.id,
    Boolean(isWebotMirrorRow)
  );

  /**
   * The same fields `seller` is resolved from, kept as one value so the identity
   * and the LINK to it can never disagree about which entry they describe.
   */
  const sellerFields = algoliaListing
    ? withRecordedOwner(algoliaListing, recordedContacts)
    : listing
    ? {
        listingOrigin: "webot" as const,
        // The authority itself — the very document the realtor edits — so there
        // is nothing to read and nothing that can be stale.
        listingContacts: listing.listingContacts ?? undefined
      }
    : undefined;

  const seller = sellerFields ? resolveListingSeller(sellerFields) : undefined;

  /**
   * The contact document behind the recorded owner, when the seller was ever
   * saved as one. Absent for a seller recorded by name and number only — which is
   * a legitimate state (see `ListingSellerDraft.createContact`), so the panel
   * affordance is simply not offered rather than opening an empty card.
   */
  const ownerContactId = sellerFields
    ? getRecordedListingContact(sellerFields)?.contactId || undefined
    : undefined;

  /**
   * A MARKET AD's owner as she SAVED him (Trello #254 follow-up, 2026-11-04).
   *
   * The board saves an ad's owner under the number she types — usually his real one, over the
   * ad's Yad2 mask — and links him to the ad. This card used to print the ad regardless: the
   * mask, a "virtual number" warning, and no way to the contact she had just made. The support
   * rep: "clicking the property still shows the owner with the virtual number, not the phone I
   * saved". It now asks the board's own question (`useSavedListingSeller` — the same stores, so
   * a dialog opened from a row costs nothing new) and shows HIM, his name opening his card.
   * Her own (`webot`) listings keep the owner recorded on the listing — see `boardSeller.ts`.
   */
  const isMarketAdView = Boolean(isViewMode && algoliaListing && algoliaListing.listingOrigin !== "webot");
  const savedSeller = useSavedListingSeller(
    algoliaListing?.objectID ?? algoliaListing?.id,
    seller?.phone,
    isMarketAdView && seller?.kind !== "agent"
  );
  const savedOwner = isMarketAdView ? savedOwnerOverAd(savedSeller, false) : null;
  const shownSeller: typeof seller =
    seller && savedOwner
      ? {
          kind: "owner",
          name: savedOwner.label ?? seller.name,
          phone: savedOwner.phone,
          isVirtualPhone: false
        }
      : seller;
  // The card to open from the seller's name: the recorded owner's, else the contact she saved
  // for this ad (through the link, or because the ad's own number is in her book).
  const sellerContactId = ownerContactId ?? (isMarketAdView ? savedSeller?.contactId : undefined);

  /**
   * The owner's contact card, opened OVER this dialog.
   *
   * Local state, not the CRM page's URL param: that param drives a panel which
   * only exists inside the CRM layout, and routing to it would close this dialog
   * and lose the listing she is looking at. See `ContactPanelDrawer`.
   */
  const [openContactId, setOpenContactId] = useState<string | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Reset tab when dialog reopens with a different initialTab
  useEffect(() => {
    if (open) setCurrentTab(initialTab ?? "general");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  useEffect(() => {
    const getListingTabsCount = async (): Promise<void> => {
      setLoading(true);
      setLoading(false);
    };

    void getListingTabsCount();
  }, [userEmail, userSettings?.virtualNumbers]);

  const { t: translate } = useTranslation();

  // Title shows the listing's address (falls back to the generic label while
  // resolving). Works for both Algolia hits (Yad2/Madlan) and webot listings.
  const titleListing = algoliaListing ?? (listing ? convertListingToProcessedListingData(listing) : undefined);
  const titleAddress = (() => {
    const addr = titleListing?.address;
    if (!addr) return "";
    const parsed = parseListingAddressByDisplayMode(addr, titleListing.addressDisplayMode);
    // Combine street + house number into a single address line. Pass it as the
    // `address` field only (not `street` too) — passing both made the street name
    // render twice, and getFullAddress never reads `houseNumber` so the number was
    // dropped entirely.
    const streetLine = parsed.street
      ? [parsed.street, parsed.houseNumber].filter(Boolean).join(" ")
      : "";
    return getFullAddress({
      address: streetLine,
      neighborhood: parsed.neighborhood,
      city: parsed.city
    });
  })();
  const dialogTitle = titleAddress || translate("listingsPage.listingDetails.title");

  type TabDef = {
    id: ListingDetailsTabType;
    count: number;
    disabled?: boolean;
  };

  // The matching tab serves BOTH markets: a rental matches renters (שוכרים)
  // against `customerBudgetRent`, exactly as a sale matches buyers — the server
  // has always done this (listingsMatch.service.ts). It used to be hidden for
  // rentals only because every label underneath it said "קונים"; those are now
  // audience-aware, so hiding it just withheld working matches from landlords.
  const tabs: TabDef[] = [
    { id: "general", count: 0 },
    { id: "buyers", count: 0 },
    { id: "creatives", count: 0 },
    { id: "makeovers", count: 0 },
    { id: "watermarks", count: 0 },
    { id: "messages", count: tabsCount?.calls ?? 0 },
    { id: "events", count: 0 },
    { id: "signatures", count: 0 },
    { id: "files", count: tabsCount?.sentMessages ?? 0, disabled: isViewMode }
  ];

  // Only the matching tab is market-dependent ("התאמת נכס לקונים" vs "…לשוכרים").
  const tabLabel = (id: ListingDetailsTabType): string =>
    translate(
      id === "buyers" && isRentListing
        ? "listingsPage.nestedRowTabs.buyersRent"
        : `listingsPage.nestedRowTabs.${id}`
    );

  // Photos offered to the AI makeovers tab. Best-effort from whichever listing
  // object we have (Algolia hit for Yad2/Madlan, or the converted webot listing).
  const makeoverImages: string[] =
    (algoliaListing as { imageUrls?: string[] } | undefined)?.imageUrls ??
    (listing ? convertListingToProcessedListingData(listing).imageUrls ?? [] : []);

  // Seed the calendar event editor with this listing so it pre-populates the
  // "listing" dropdown when the user creates a task/meeting from the events tab.
  // For Webot listings (parent already provides handleAddNewTask) this is a no-op
  // because the parent handler bypasses CalendarTasks's internal openModal.
  const calendarCustomEventProps: Partial<CalendarEvent> | undefined =
    isViewMode && algoliaListing
      ? { listingIds: [algoliaListing.objectID], listingsData: [algoliaListing] }
      : listing
      ? {
          listingIds: [listing.id],
          listingsData: [
            { ...convertListingToProcessedListingData(listing), id: listing.id }
          ]
        }
      : undefined;

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="md"
      PaperProps={{ sx: { height: "100%" } }}
      onClose={handleClose}
    >
      <DialogTitle sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap title={dialogTitle}>
              {dialogTitle}
            </Typography>
            {listingId && <ListingNotesPopover listingId={listingId} alwaysListen />}
            {/* Meetings and tasks on the listing at a glance: one icon, the next one in the tooltip, the list on click. */}
            {listingId && <ListingActivityIndicator events={listingEvents.events} />}
            {listingId && <ListingOrgActivityIndicator listingId={listingId} />}
          </Stack>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isResolvingListing && !algoliaListing && !listing ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
            <CircularProgress />
          </Stack>
        ) : (
        <TabContext value={currentTab}>
          {isSmallScreen ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={currentTab}
                onChange={(e) => setCurrentTab(e.target.value as ListingDetailsTabType)}
              >
                {tabs.map(({ id, count, disabled }) => (
                  <MenuItem key={id} value={id} disabled={disabled}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
                      <span>{tabLabel(id)}</span>
                      {id !== "general" && id !== "buyers" && id !== "creatives" && id !== "makeovers" && id !== "watermarks" && !loading && !!count && (
                        <Label color="default">{count}</Label>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TabList
              sx={{
                mb: 2,
                // The theme spaces tabs 40px apart (fine for 3-4 page tabs, not for
                // the 9 tabs this dialog now has — they overflowed the md dialog and
                // made the strip unusable). Tighten the gap locally and trim the tab
                // padding so the full set fits at normal dialog width.
                "& .MuiTab-root:not(:last-of-type)": { marginRight: 2 },
                "& .MuiTab-root": { px: 0.5, minWidth: "unset" }
              }}
              value={currentTab}
              variant="scrollable"
              scrollButtons="auto"
              // Keep the left/right scroll arrows available at every width the
              // TabList renders at (the <600px breakpoint uses a Select instead,
              // but 600-900px dialogs still need them when the tabs overflow).
              allowScrollButtonsMobile
              onChange={(_, newValue) =>
                setCurrentTab(newValue as ListingDetailsTabType)
              }
            >
              {tabs.map(({ id, count, disabled }) => {
                const showBadge =
                  id !== "general" && id !== "buyers" && id !== "creatives" && id !== "makeovers" && id !== "watermarks";
                return (
                  <Tab
                    value={id}
                    disabled={disabled}
                    key={id}
                    label={
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        <span>{tabLabel(id)}</span>
                        {showBadge && loading && (
                          <Skeleton
                            sx={{ borderRadius: 1 }}
                            variant="circular"
                            width={18}
                            height={18}
                          />
                        )}
                        {showBadge && !loading && !!count && (
                          <Label color="default">{count}</Label>
                        )}
                      </Stack>
                    }
                  />
                );
              })}
            </TabList>
          )}
          <TabPanel value="general">
            {isViewMode && algoliaListing ? (
              <Stack gap={2}>
                <ListingDetailsNew
                  listing={algoliaListing}
                  listingLink=""
                  moreListingsData={[]}
                  externalListingUrl={
                    algoliaListing.listingOrigin !== "webot"
                      ? algoliaListing.originalListingUrl
                      : undefined
                  }
                  externalListingSourceLabel={
                    algoliaListing.listingOrigin === "yad2"
                      ? "Yad2"
                      : algoliaListing.listingOrigin === "madlan"
                      ? "Madlan"
                      : undefined
                  }
                />
                {/* ⚠ NOT `algoliaListing.sellerName/sellerPhoneNumber` — see the
                    `seller` note above. Those are the LISTING AGENT on a webot
                    row, and rendering them here is what showed a realtor her own
                    number, with a live tel: button, under "מפרסם". */}
                <ListingSellerIdentityCard
                  seller={shownSeller}
                  caption={savedOwner ? translate("listingsPage.publisher.savedOwnerCaption") : undefined}
                  onOpenContact={sellerContactId ? () => setOpenContactId(sellerContactId) : undefined}
                  isResolvingOwner={isWebotMirrorRow && recordedContacts === undefined}
                  whatsAppMessage={
                    algoliaListing.originalListingUrl
                      ? translate("listingsPage.publisher.whatsAppQuestion", "שאלה לגבי ") +
                        algoliaListing.originalListingUrl
                      : undefined
                  }
                />
              </Stack>
            ) : listing ? (
              <Stack gap={2}>
                <ListingDetailsGeneralTab
                  listing={listing}
                  isAuthorized={isAuthorized}
                  handleClickFavorite={handleClickFavorite}
                  handleEditListingDialogOpen={handleEditListingDialogOpen}
                />
                {/* The general tab's own publisher card renders `ownersData[0]` —
                    the REALTOR. That is a true thing to show on her own listing,
                    but it is not the seller, and before this the owner she
                    recorded appeared on no screen at all. Added here rather than
                    inside that tab so the two cards stay visibly distinct: one is
                    "who is marketing this", the other "whose property it is". */}
                {seller?.kind === "owner" && (
                  <ListingSellerIdentityCard
                    seller={seller}
                    onOpenContact={
                      ownerContactId ? () => setOpenContactId(ownerContactId) : undefined
                    }
                  />
                )}
              </Stack>
            ) : null}
            {/* Everything on this listing, hers and her office's: meetings, tasks and notes in one timeline. */}
            {listingId && <ListingActivitySection listingId={listingId} events={listingEvents.events} />}
          </TabPanel>
          <TabPanel value="buyers">
            {algoliaListing ? (
              <MatchedBuyersTab listing={algoliaListing} listingId={algoliaListing.objectID} />
            ) : listing ? (
              <MatchedBuyersTabForWebot listingId={listing.id} listingStatus={listing.status} isRentListing={isRentListing} />
            ) : null}
          </TabPanel>
          <TabPanel value="creatives">
            <ListingCreativesTab listingId={listingId} />
          </TabPanel>
          <TabPanel value="makeovers">
            <ListingMakeoversTab listingId={listingId} images={makeoverImages} />
          </TabPanel>
          <TabPanel value="watermarks">
            <ListingWatermarksTab listingId={listingId} />
          </TabPanel>
          <TabPanel value="messages">
            <ListingDetailsMessagesTab listingId={listingId} />
          </TabPanel>
          <TabPanel value="events">
            <CalendarTasks
              listingEvents={listingEvents}
              customEventProps={calendarCustomEventProps}
              onAddNewTask={handleAddNewTask}
            />
          </TabPanel>
          <TabPanel value="signatures">
            <ListingTableSignatures listingId={listingId} isRentListing={isRentListing} />
          </TabPanel>
          <TabPanel value="files">
            {!isViewMode && listing && (
              <ListingTableFiles listing={listing} isAuthorized={isAuthorized} />
            )}
            {isViewMode && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  {translate("listingDetail.files.notAvailable", "אין קבצים לנכס זה")}
                </Typography>
              </Box>
            )}
          </TabPanel>
        </TabContext>
        )}
      </DialogContent>
      {/* Rendered INSIDE the Dialog so it unmounts with it, but painted above it —
          the Drawer raises its own z-index past the modal layer. Keeping it here
          rather than at a page root is what lets any host of this dialog get the
          panel for free. */}
      <ContactPanelDrawer contactId={openContactId} onClose={() => setOpenContactId(null)} />
    </Dialog>
  );
}

/**
 * The seller side of a listing, labelled by WHO it actually is.
 *
 * `ListingPublisherCard` renders a name, a phone, a `tel:` button and a WhatsApp
 * button and says nothing about whose they are — which is fine for a market ad
 * and actively misleading on a claimed one. `resolveListingSeller` has already
 * decided; this only has to say it out loud, and refuse to call the LISTING AGENT
 * the seller.
 */
function ListingSellerIdentityCard({
  seller,
  caption: captionOverride,
  isResolvingOwner = false,
  whatsAppMessage,
  onOpenContact
}: {
  seller: ReturnType<typeof resolveListingSeller> | undefined;
  /**
   * Says where the person shown came from, when it is not what `seller.kind` implies — an
   * owner she SAVED for a market ad is not "as recorded on your listing".
   */
  caption?: string;
  /**
   * Opens this person's contact card. Passed only when there IS one — a seller
   * recorded on the listing without a CRM contact has nothing to open, and a
   * button that opens an empty card is worse than no button.
   */
  onOpenContact?: () => void;
  /**
   * The authority read for the recorded owner has not landed yet. Until it does,
   * a `kind: 'agent'` result means "we do not know", not "nobody is recorded" —
   * so the "no owner recorded" line is withheld rather than flashed and retracted.
   */
  isResolvingOwner?: boolean;
  whatsAppMessage?: string;
}): JSX.Element | null {
  const { t: translate } = useTranslation();

  if (!seller) return null;

  const heading =
    seller.kind === "owner"
      ? translate("listingsPage.publisher.ownerHeading")
      : seller.kind === "agent"
      ? translate("listingsPage.publisher.agentHeading")
      : translate("listingsPage.publisher.sellerHeading");

  const caption = captionOverride
    ? captionOverride
    : seller.kind === "owner"
      ? translate("listingsPage.publisher.ownerCaption")
      : seller.kind === "agent"
      ? // Says the two things that are true and were previously implied to be
        // false: these are the agent's details, and no owner is recorded.
        translate("listingsPage.publisher.agentCaption")
      : undefined;

  if (!seller.name && !seller.phone && !seller.agencyName) return null;

  return (
    <Stack gap={0.5}>
      <Typography variant="subtitle2">{heading}</Typography>
      {caption && !isResolvingOwner && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
      {seller.isVirtualPhone && (
        <Typography variant="caption" color="warning.main">
          {translate("listingsBoard.seller.maskedPhone")}
        </Typography>
      )}
      {/* The NAME is the control — no separate labelled row. A link that said
          "open the contact card in a new tab" used to sit here, which was wrong
          twice: it was borrowed copy from the board badge (which really does open
          a tab) while this opens a side panel, and it spent a whole row saying
          what clicking a person's name already says. */}
      <ListingPublisherCard
        name={seller.name}
        phoneNumber={seller.phone}
        agencyName={seller.agencyName}
        whatsAppMessage={whatsAppMessage}
        onNameClick={onOpenContact}
      />
    </Stack>
  );
}

/**
 * The last mirror row read per listing id, POSITIVE results only. Reopening a listing (or coming
 * back to this tab — the TabPanel unmounts it) used to show a 200px skeleton for a fresh read every
 * time; now it renders from here at once and re-reads in the background. A negative result is never
 * cached: "no mirror yet" is the brand-new-listing state and must be re-checked on every open.
 */
const mirrorHitCache = new Map<string, AlgoliaHitResult>();
const MAX_MIRROR_HITS = 100;

function rememberMirrorHit(listingId: string, hit: AlgoliaHitResult): void {
  mirrorHitCache.delete(listingId);
  mirrorHitCache.set(listingId, hit);
  while (mirrorHitCache.size > MAX_MIRROR_HITS) {
    const oldest = mirrorHitCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    mirrorHitCache.delete(oldest);
  }
}

/** Cheap "did the row change" test so a background re-read does not re-render identical data. */
function sameHit(a: AlgoliaHitResult | null, b: AlgoliaHitResult | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * The mirror Cloud Function (`updateWebotListingsOnAlgolia`, functions/src/index.ts) writes the
 * `yad2-listings-latest` row ONLY while the listing's status is `onSale`, and DELETES it on any
 * other status — so a sold / frozen / removed / in-negotiation listing has no mirror, and matching
 * (which reads the mirror) has nothing to match.
 */
const MIRRORED_LISTING_STATUS: ListingStatus = "onSale";

// MatchedBuyersTab expects an AlgoliaHitResult; for Webot listings we fetch
// the corresponding listing doc by id on demand.
function MatchedBuyersTabForWebot({
  listingId,
  listingStatus,
  isRentListing
}: {
  listingId: string;
  /** The authority's status — decides between "not active" and "still syncing" when there is no mirror. */
  listingStatus: ListingStatus | undefined;
  /** Names the right status option ("להשכרה" vs "למכירה") and audience in the not-active hint. */
  isRentListing: boolean;
}): JSX.Element {
  const { t: translate } = useTranslation();
  const isMirroredStatus = !listingStatus || listingStatus === MIRRORED_LISTING_STATUS;
  // An inactive listing is not served from the cache: its mirror is being (or was) deleted, and a
  // remembered row would show buyers for a sold listing until the re-read lands.
  const cachedHit = (): AlgoliaHitResult | null =>
    isMirroredStatus ? mirrorHitCache.get(listingId) ?? null : null;
  const [algoliaListing, setAlgoliaListing] = useState<AlgoliaHitResult | null>(cachedHit);
  const [loading, setLoading] = useState(() => !cachedHit());

  useEffect(() => {
    let cancelled = false;
    const cached = isMirroredStatus ? mirrorHitCache.get(listingId) ?? null : null;
    setAlgoliaListing(cached);
    setLoading(!cached);

    // Background re-read even on a cache hit: the row may have changed since.
    fetchListingById(listingId)
      .then((hit) => {
        if (cancelled) return;
        if (hit) {
          rememberMirrorHit(listingId, hit);
          setAlgoliaListing((current) => (sameHit(current, hit) ? current : hit));
        } else if (!isMirroredStatus) {
          // No mirror because the listing is not active: drop what we had, so a listing that was
          // just marked sold stops showing buyers.
          mirrorHitCache.delete(listingId);
          setAlgoliaListing(null);
        }
        // `null` for an active listing keeps what is on screen: fetchListingById also returns null
        // after a failed read, and an active listing's mirror does not disappear on its own.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [listingId, isMirroredStatus]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (!algoliaListing && !isMirroredStatus) {
    // Not a sync delay and not "nobody matches": this listing will never have a mirror while its
    // status stays what it is. Saying "still syncing" here promised buyers that never came.
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Stack gap={0.5} alignItems="center">
          <Typography color="text.secondary">
            {translate("listingsPage.matchedBuyers.listingNotActive")}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {translate(
              isRentListing
                ? "listingsPage.matchedBuyers.listingNotActiveHintRent"
                : "listingsPage.matchedBuyers.listingNotActiveHint"
            )}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!algoliaListing) {
    /**
     * ⚠ THIS IS A REACHABLE, EXPECTED STATE ON A BRAND-NEW LISTING — not an error,
     * and not "no buyers".
     *
     * This tab re-reads the `yad2-listings-latest` MIRROR by id, and the mirror
     * document does not exist until the `updateWebotListingsOnAlgolia` Cloud
     * Function has run. The board's optimistic just-claimed row is on screen
     * within a second of the save, so a realtor who claims an ad, clicks the row
     * and taps קונים genuinely arrives here before anything has been written —
     * a window this pass made reachable, since the board used to hand the tab the
     * market-ad hit it was already holding. It used to say "No matching data
     * available." — untranslated LTR English inside an RTL Hebrew dialog, on the
     * newest journey in the product.
     *
     * A generic empty state would be no better: it would read as "nobody matches
     * your property", which is a different and wrong answer. Say that the listing
     * is still syncing and the buyers will follow.
     */
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Stack gap={0.5} alignItems="center">
          <Typography color="text.secondary">
            {translate("listingsPage.matchedBuyers.listingStillSyncing")}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {translate("listingsPage.matchedBuyers.listingStillSyncingHint")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return <MatchedBuyersTab listing={algoliaListing} listingId={listingId} />;
}

const listingDetailsTabIds = [
  "general",
  "buyers",
  "creatives",
  "makeovers",
  "watermarks",
  "messages",
  "events",
  "signatures",
  "files"
] as const;

export type ListingDetailsTabType = (typeof listingDetailsTabIds)[number];
